import { FieldSchemaEditorSupportedTypes } from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { getAllFieldKeys, getAllFields } from 'dashboard/components-lib/FieldSchemaEditor/utils';
import { FieldSchemaType } from 'lib/fields';

const findNonUniqueKeys = (fieldSchemas: FieldSchemaEditorSupportedTypes[]): string[] => {
  const uniqueKeys = new Set<string>();
  const nonUniqueKeys = new Set<string>();
  const keyCollection = getAllFieldKeys(fieldSchemas);
  for (let i = 0; i < keyCollection.length; i++) {
    if (uniqueKeys.has(keyCollection[i])) {
      nonUniqueKeys.add(keyCollection[i]);
    }
    uniqueKeys.add(keyCollection[i]);
  }
  return Array.from(nonUniqueKeys);
};

export const singleFieldSchemaError = (
  singleSchema: FieldSchemaEditorSupportedTypes,
): { message: string; validForDraft: boolean } => {
  if (!singleSchema.key || !/^[a-zA-Z]/.test(singleSchema.key)) {
    return {
      message: 'Field keys must not be empty and must begin with a letter',
      validForDraft: false,
    };
  }

  if (singleSchema.key.includes('.')) {
    return { message: 'Field keys cannot contain "."', validForDraft: false };
  }

  if (!singleSchema.type) {
    return { message: 'Field type must be selected', validForDraft: true };
  }
  if (
    singleSchema.type === FieldSchemaType.MultiChoices &&
    (!singleSchema.config.options || singleSchema.config.options.length === 0)
  ) {
    return { message: 'Field options cannot be empty', validForDraft: true };
  }

  if (
    singleSchema.type === FieldSchemaType.MultiChoices &&
    singleSchema.config.options &&
    singleSchema.config.options.map((item) => item.trim()).length !==
      new Set(singleSchema.config.options.map((item) => item.trim())).size // check if there is a duplicate option
  ) {
    return { message: 'Field options cannot contain duplicates', validForDraft: false };
  }

  if (singleSchema.type === FieldSchemaType.Array) {
    if (!singleSchema.config['item-schema']?.type) {
      return { message: 'Array type cannot be empty', validForDraft: true };
    }
    if (
      singleSchema.config['item-schema']?.type === FieldSchemaType.Hash &&
      singleSchema.config['item-schema']?.config['item-schemas']?.length === 0
    ) {
      return { message: 'Hash type cannot be empty', validForDraft: true };
    }
  }
  if (
    singleSchema.type === FieldSchemaType.Hash &&
    singleSchema.config['item-schemas']?.length === 0
  ) {
    return { message: 'Hash type cannot be empty', validForDraft: true };
  }
  return { message: '', validForDraft: true };
};

export const validateAllSchemas = (fieldSchemas: FieldSchemaEditorSupportedTypes[]): string[] => {
  return Object.values(getAllFields(fieldSchemas))
    .filter(({ key, fieldSchema }) => {
      const { message } = singleFieldSchemaError(fieldSchema);
      return !key.endsWith('.[]') && !!message;
    })
    .map(({ key }) => key)
    .concat(findNonUniqueKeys(fieldSchemas));
};

// validForDraft is a boolean representing if a field schema is safe to add to a list of
// field schemas. If we are not able to access each field schema in the array of field schemas
// it is not safe to add to draft.
// Example: field schema has empty key or has same key as existing field schema
export const validateSingleFieldSchema = (
  singleSchema: FieldSchemaEditorSupportedTypes,
  allSchemas: FieldSchemaEditorSupportedTypes[],
): { message: string; validForDraft: boolean } => {
  const nonUniqueKeys = findNonUniqueKeys(allSchemas);

  if (nonUniqueKeys.some((key) => key.split('.').slice(-1)[0] === singleSchema.key)) {
    return { message: `Field ${singleSchema.key} already exists`, validForDraft: false };
  }
  return singleFieldSchemaError(singleSchema);
};
